import React, { useState } from "react";
import Panel from "ui/Panel";

import Grid, { GridColumn } from "ui/Grid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClonePlus, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import {
  QueryParameterValue,
  stringifyValue,
  workbookDetailsRefetch,
} from "features/Workbooks/WorkbookList/Workbook/util";
import { VariantPanel } from "features/Workbooks/WorkbookList/WorkbookVariant";

import { WorkbookDetailsQuery } from "features/Workbooks/WorkbookList/Workbook/gql/Query.generated";
import { useDeleteWorkbookParameterSetsMutation } from "features/Workbooks/WorkbookList/ChooseParameters/gql/Mutation.delete.generated";
export type WorkbookType = WorkbookDetailsQuery["getWorkbookDetails"];

import ModalContainer from "ui/ModalContainer";
import CreateParameterSetPanel from "features/Workbooks/WorkbookList/CreateParameterSetPanel";
import Callout from "ui/Callout";
import Popover from "ui/Popover";
import Button from "ui/Button";

const ParameterSetsPanel = ({
  databaseId,
  workbook,
}: {
  workbook: WorkbookType;
  databaseId: string;
}) => {
  const [showOnlyDiff, setShowOnlyDiff] = useState(false);
  const [deleteWorkbookParameterSets] =
    useDeleteWorkbookParameterSetsMutation();
  const [showAddParameterSet, setShowAddParameterSet] = useState(false);
  // When set, the "Add Parameter Set" modal opens pre-filled from this set's values.
  const [copyFromId, setCopyFromId] = useState<string | undefined>(undefined);
  const hasMaxParameterSetLength = workbook.parameterSets.length === 5;

  const openAddParameterSet = (fromId?: string) => {
    setCopyFromId(fromId);
    setShowAddParameterSet(true);
  };

  const secondaryTitle = (
    <div className="flex">
      <input
        type="checkbox"
        checked={showOnlyDiff}
        id="show_only_diff"
        onChange={(evt) => setShowOnlyDiff(evt.target.checked)}
      />{" "}
      <label htmlFor="show_only_diff">
        Show only parameters with different values
      </label>
    </div>
  );

  const paramSetsCount = workbook.parameterSets.length;
  const aliasList = workbook.parameterRefAliases;
  const data = aliasList.map((rawAlias) => {
    const alias = `$${rawAlias}`;
    const dat: Record<string, { value: QueryParameterValue; type?: string }> = {
      alias: { value: alias },
    };
    workbook.aliasParamMapList.forEach((val) => {
      const colName = `set${val.id}`;
      // val.parameters: { "$alias1": { value: "foo", type: "text" }, ... }
      dat[colName] = val.parameters[alias];
    });
    return dat;
  });
  const columns: GridColumn<
    (typeof data)[number],
    keyof (typeof data)[number]
  >[] = [
    {
      field: "alias",
      header: "",
      renderer: ({ fieldData }) => fieldData.value,
      className: "font-medium",
      width: "1fr",
      disableSort: true,
    },
  ];
  let valueWidth = "15%";
  switch (paramSetsCount) {
    case 1:
      valueWidth = "50%";
      break;
    case 2:
      valueWidth = "40%";
      break;
    case 3:
      valueWidth = "25%";
      break;
  }

  workbook.aliasParamMapList.forEach((aliasParamMap) => {
    columns.push({
      field: `set${aliasParamMap.id}`,
      header: (
        <div>
          {aliasParamMap.name}
          <Button
            bare
            onClick={() => {
              if (hasMaxParameterSetLength) {
                return;
              }
              openAddParameterSet(aliasParamMap.id);
            }}
            title={
              hasMaxParameterSetLength
                ? "Up to 5 parameter sets can be created"
                : "Create a new parameter set from this one"
            }
            className={`ml-2 ${
              hasMaxParameterSetLength
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#606060] cursor-pointer"
            }`}
          >
            <FontAwesomeIcon icon={faClonePlus} />
          </Button>
          {workbook.aliasParamMapList.length > 1 && (
            <Button
              bare
              onClick={() => {
                if (
                  window.confirm(
                    `Delete parameter set "${aliasParamMap.name}"? This will delete all EXPLAIN results for this parameter set.`,
                  )
                ) {
                  deleteWorkbookParameterSets({
                    variables: {
                      workbookId: workbook.id,
                      parameterSetId: aliasParamMap.id,
                    },
                    ...workbookDetailsRefetch(workbook.id, databaseId),
                  });
                }
              }}
              title="Delete"
              className="text-[#CA1515] ml-2 cursor-pointer"
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </Button>
          )}
        </div>
      ),
      renderer: function ParamValueCell({ fieldData }) {
        if (!fieldData) {
          // Avoid the race condition of trying to render this while the filteredData is not updated yet
          return null;
        }
        return (
          <span title={stringifyValue(fieldData.value, fieldData.type)}>
            {stringifyValue(fieldData.value, fieldData.type)}
          </span>
        );
      },
      width: valueWidth,
      disableSort: true,
    });
  });

  const filteredData = data.filter((dat) => {
    if (showOnlyDiff) {
      const colNames = workbook.parameterSets.map((val) => `set${val.id}`);
      const values = colNames.map((colName) => dat[colName]);
      return values.some((value) => value.value !== values[0].value);
    }
    return true;
  });

  return (
    <>
      <VariantPanel workbook={workbook} variant={workbook.baselineQuery} />
      <div className="mb-4 text-right">
        <Popover
          content={
            hasMaxParameterSetLength &&
            "Only up to 5 parameter sets can be created"
          }
        >
          <button
            className="btn btn-primary"
            onClick={() => openAddParameterSet()}
            disabled={hasMaxParameterSetLength}
          >
            Add parameter set
          </button>
        </Popover>
      </div>
      <Panel title="Parameter Sets" secondaryTitle={secondaryTitle}>
        <Grid data={filteredData} columns={columns} />
      </Panel>
      {showAddParameterSet && (
        <AddParameterSetModal
          onDismiss={() => setShowAddParameterSet(false)}
          workbook={workbook}
          initialCopyFromId={copyFromId}
        />
      )}
    </>
  );
};

const AddParameterSetModal = ({
  onDismiss,
  workbook,
  initialCopyFromId,
}: {
  onDismiss: () => void;
  workbook: WorkbookType;
  initialCopyFromId?: string;
}) => {
  return (
    <ModalContainer
      title="Add Parameter Set"
      layout="centered"
      onClose={onDismiss}
    >
      <Callout className="mb-4">
        After adding a parameter set, you can add its missing EXPLAIN plans for
        each variant by going to each variant's page and either uploading
        EXPLAIN results manually or re-running the collector workflow.
      </Callout>
      <CreateParameterSetPanel
        workbook={workbook}
        addParamMethod="manual"
        onParameterSetsCreated={onDismiss}
        initialCopyFromId={initialCopyFromId}
      />
    </ModalContainer>
  );
};

export default ParameterSetsPanel;
